import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Chip,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

function ConfiguracionAuxiliares() {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();
  const { tienePermiso } = useAccesos();

  // Opciones predefinidas de tabla auxiliar (sistema simplificado)
  const opcionesTablasAuxiliares = [
    { value: 'mg_clientes', label: 'Clientes' },
    { value: 'mg_proveedores', label: 'Proveedores' },
    { value: 'ctb_prestamo_bancario', label: 'Préstamos Bancarios' },
    { value: 'pl_empleados', label: 'Empleados' },
    { value: 'mg_origenes', label: 'Empresas/Orígenes' },
    { value: 'mg_medicos', label: 'Médicos' }
  ];

  const [loading, setLoading] = useState(false);
  const [configuraciones, setConfiguraciones] = useState([]);
  const [tiposAuxiliar, setTiposAuxiliar] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    idCuenta: null,
    idTipoAuxiliar: null,
    idCuentaContrapartida: null,
    tablaAuxiliar: '',
    campoCodigo: '',
    campoDescripcion: '',
    filtroAdicional: '',
    obligatorio: true,
    activo: true,
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar configuraciones de auxiliares
      try {
        console.log('🔄 Cargando configuraciones desde:', `${server}/ctb/config-auxiliares`);
        const configRes = await fetch(`${server}/ctb/config-auxiliares`);
        console.log('📡 Respuesta status:', configRes.status, configRes.ok);
        
        if (configRes.ok) {
          const configData = await configRes.json();
          console.log('📦 Datos recibidos (raw):', configData);
          console.log('📦 configData.data:', configData.data);
          console.log('📦 configData.data.$values:', configData.data?.$values);
          console.log('📦 Array.isArray(configData.data):', Array.isArray(configData.data));
          
          // Asegurar que siempre sea un array - el backend devuelve { exito, data: { $values: [...] }, total }
          const configArray = Array.isArray(configData.data) 
            ? configData.data 
            : (Array.isArray(configData.data?.$values) 
              ? configData.data.$values 
              : (Array.isArray(configData.$values) ? configData.$values : (Array.isArray(configData) ? configData : [])));
          
          console.log('✅ Array final (length):', configArray.length);
          console.log('✅ Array final:', configArray);
          setConfiguraciones(configArray);
        } else {
          console.warn('⚠️ No se pudieron cargar las configuraciones');
          setConfiguraciones([]);
        }
      } catch (err) {
        console.error('❌ Error al cargar configuraciones:', err);
        setConfiguraciones([]);
      }

      // Cargar tipos de auxiliar
      try {
        const tiposRes = await fetch(`${server}/CtbTipoAuxiliars`);
        if (tiposRes.ok) {
          const tiposData = await tiposRes.json();
          setTiposAuxiliar(tiposData.$values || tiposData || []);
        } else {
          console.warn('No se pudieron cargar los tipos de auxiliar');
          setTiposAuxiliar([]);
        }
      } catch (err) {
        console.warn('Error al cargar tipos de auxiliar:', err);
        setTiposAuxiliar([]);
      }

      // Cargar plan de cuentas - solo nivel de detalle (nivel 5 que acepta movimientos)
      try {
        const cuentasRes = await fetch(`${server}/CtbPlanDeCuentas/detalle`);
        if (cuentasRes.ok) {
          const cuentasData = await cuentasRes.json();
          console.log('📦 Respuesta cuentas (raw):', cuentasData);
          
          // El endpoint /detalle ya devuelve solo cuentas de nivel 5
          const cuentasArray = cuentasData.$values || cuentasData || [];
          console.log('📊 Total cuentas recibidas:', cuentasArray.length);
          
          if (cuentasArray.length > 0) {
            console.log('🔍 Primera cuenta (ejemplo):', cuentasArray[0]);
          }
          
          // Filtrar solo cuentas de detalle - usar minúsculas y mayúsculas por compatibilidad
          const cuentasDetalle = cuentasArray.filter(cuenta => {
            const nivel = cuenta.nivel || cuenta.Nivel;
            const aceptaMov = cuenta.aceptamovimientos || cuenta.aceptaMovimientos || cuenta.AceptaMovimientos;
            return nivel === 5 && aceptaMov === true;
          });
          
          console.log('✅ Cuentas de detalle filtradas:', cuentasDetalle.length);
          setCuentas(cuentasDetalle);
        } else {
          console.warn('No se pudieron cargar las cuentas contables');
          setCuentas([]);
        }
      } catch (err) {
        console.warn('Error al cargar cuentas contables:', err);
        setCuentas([]);
      }
    } catch (error) {
      console.error('Error general al cargar datos:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al cargar los datos.',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      id: null,
      idCuenta: null,
      idTipoAuxiliar: null,
      idCuentaContrapartida: null,
      tablaAuxiliar: '',
      campoCodigo: null, // Campo obsoleto
      campoDescripcion: null, // Campo obsoleto
      filtroAdicional: null, // Campo obsoleto
      obligatorio: true,
      activo: true,
    });
    setEditMode(false);
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      console.log('🔍 Cargando configuración para editar:', config);
      console.log('📊 Cuentas disponibles:', cuentas.length);
      
      // Buscar las cuentas correspondientes usando idCuenta (no id)
      const cuenta = cuentas.find(c => 
        (c.idCuenta || c.id) === config.idCuenta
      );
      const cuentaContrapartida = config.idCuentaContrapartida 
        ? cuentas.find(c => (c.idCuenta || c.id) === config.idCuentaContrapartida)
        : null;
      const tipoAux = tiposAuxiliar.find(t => t.id === config.idTipoAuxiliar);

      console.log('✅ Cuenta encontrada:', cuenta);
      console.log('✅ Cuenta contrapartida encontrada:', cuentaContrapartida);
      console.log('✅ Tipo auxiliar encontrado:', tipoAux);

      setForm({
        id: config.id,
        idCuenta: cuenta || null,
        idTipoAuxiliar: tipoAux || null,
        idCuentaContrapartida: cuentaContrapartida || null,
        tablaAuxiliar: config.tablaAuxiliar || '',
        campoCodigo: config.campoCodigo || '',
        campoDescripcion: config.campoDescripcion || '',
        filtroAdicional: config.filtroAdicional || '',
        obligatorio: config.obligatorio ?? true,
        activo: config.activo ?? true,
      });
      setEditMode(true);
    } else {
      resetForm();
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!form.idCuenta) {
      Swal.fire('Validación', 'Seleccione una cuenta contable.', 'warning');
      return;
    }
    if (!form.idTipoAuxiliar) {
      Swal.fire('Validación', 'Seleccione un tipo de auxiliar.', 'warning');
      return;
    }
    if (!form.tablaAuxiliar || !form.tablaAuxiliar.trim()) {
      Swal.fire('Validación', 'Seleccione una tabla auxiliar.', 'warning');
      return;
    }
    
    // Validar que la tabla auxiliar sea una de las permitidas
    const tablasValidas = opcionesTablasAuxiliares.map(opt => opt.value);
    if (!tablasValidas.includes(form.tablaAuxiliar)) {
      Swal.fire('Validación', 'La tabla auxiliar seleccionada no es válida.', 'warning');
      return;
    }

    // Obtener IDs de forma robusta
    const getIdFromObject = (obj) => {
      if (!obj) return null;
      // Buscar en diferentes propiedades posibles
      return obj.idCuenta || obj.id || obj.Id || obj.ID || obj.idTipoAuxiliar || null;
    };

    // Formatear usuario a 20 caracteres
    const usuarioFormateado = (user?.codigoUsuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);

    const payload = {
      idCuenta: getIdFromObject(form.idCuenta),
      idTipoAuxiliar: getIdFromObject(form.idTipoAuxiliar),
      idCuentaContrapartida: getIdFromObject(form.idCuentaContrapartida),
      tablaAuxiliar: form.tablaAuxiliar.trim(),
      campoCodigo: null, // Campo obsoleto - sistema predefinido
      campoDescripcion: null, // Campo obsoleto - sistema predefinido
      filtroAdicional: null, // Campo obsoleto - sistema predefinido
      obligatorio: form.obligatorio,
      activo: form.activo,
    };

    // Agregar ID si es edición
    if (editMode) {
      payload.id = form.id;
    }

    // Agregar campo de auditoría según el modo
    if (editMode) {
      payload.modificadoPor = usuarioFormateado;
    } else {
      payload.ingresadoPor = usuarioFormateado;
    }

    console.log('📤 Payload a enviar:', payload);
    console.log('🔄 Modo edición:', editMode);
    console.log('🆔 ID en payload:', payload.id);

    try {
      setLoading(true);
      const url = editMode
        ? `${server}/ctb/config-auxiliares/${form.id}`
        : `${server}/ctb/config-auxiliares`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('❌ Error del backend:', errorData);
        console.error('❌ Response completo:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        
        // Intentar extraer el mensaje de error más específico
        const mensaje = errorData?.mensaje 
          || errorData?.message 
          || errorData?.error
          || errorData?.title
          || (typeof errorData === 'string' ? errorData : null)
          || `Error HTTP ${response.status}`;
        
        throw new Error(mensaje);
      }

      Swal.fire('Éxito', `Configuración ${editMode ? 'actualizada' : 'creada'} correctamente.`, 'success');
      handleCloseModal();
      await cargarDatos();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      Swal.fire('Error', error.message || 'No se pudo guardar la configuración.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActivo = async (config) => {
    try {
      const response = await fetch(
        `${server}/ctb/config-auxiliares/${config.id}/toggle-active`,
        { method: 'PATCH' }
      );

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      Swal.fire('Éxito', 'Estado actualizado correctamente.', 'success');
      await cargarDatos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Swal.fire('Error', 'No se pudo cambiar el estado.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const usuarioFormateado = (user?.usuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);
        const response = await fetch(
          `${server}/ctb/config-auxiliares/${id}/${encodeURIComponent(usuarioFormateado)}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          throw new Error(`Error HTTP ${response.status}`);
        }

        Swal.fire('Eliminado', 'Configuración eliminada correctamente.', 'success');
        await cargarDatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        Swal.fire('Error', 'No se pudo eliminar la configuración.', 'error');
      }
    }
  };

  const getCuentaDisplay = (cuenta) => {
    if (!cuenta) return '';
    return `${cuenta.codigo || ''} - ${cuenta.descripcion || cuenta.Descripcion || ''}`;
  };

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>⚙️ Configuración de Auxiliares Contables</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Configuraciones Registradas
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={cargarDatos}
                      disabled={loading}
                      sx={{ mr: 1 }}
                    >
                      Recargar
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenModal()}
                      disabled={loading || !tienePermiso('Configurar Auxiliares')}
                    >
                      Nueva Configuración
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>Tipo Auxiliar</TableCell>
                        <TableCell>Cuenta Contable</TableCell>
                        <TableCell>Cuenta Contrapartida</TableCell>
                        <TableCell>Tipo de Auxiliar</TableCell>
                        <TableCell align="center">Obligatorio</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            Cargando...
                          </TableCell>
                        </TableRow>
                      ) : configuraciones.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            No hay configuraciones registradas
                            {console.log('🔍 Renderizando: configuraciones.length =', configuraciones.length)}
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {console.log('🎨 Renderizando', configuraciones.length, 'configuraciones')}
                          {configuraciones.map((config) => {
                            console.log('📝 Renderizando config:', config);
                            return (
                          <TableRow key={config.id} hover>
                            <TableCell>
                              <Chip
                                label={config.idTipoAuxiliarNavigation?.tipoAuxiliar || config.tipoAuxiliar?.tipoAuxiliar || 'N/A'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {getCuentaDisplay(config.idCuentaNavigation || config.cuentaPrincipal)}
                            </TableCell>
                            <TableCell>
                              {config.idCuentaContrapartida 
                                ? getCuentaDisplay(config.idCuentaContrapartidaNavigation || config.cuentaContrapartida)
                                : <Typography variant="body2" color="textSecondary">-</Typography>
                              }
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={opcionesTablasAuxiliares.find(opt => opt.value === config.tablaAuxiliar)?.label || config.tablaAuxiliar}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {config.obligatorio ? (
                                <Chip label="Sí" size="small" color="warning" />
                              ) : (
                                <Chip label="No" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={config.activo ? 'Desactivar' : 'Activar'}>
                                <Switch
                                  checked={config.activo ?? false}
                                  onChange={() => handleToggleActivo(config)}
                                  size="small"
                                  color="success"
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenModal(config)}
                                  disabled={!tienePermiso('Configurar Auxiliares')}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(config.id)}
                                  disabled={!tienePermiso('Configurar Auxiliares')}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                        })}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Modal de Formulario */}
        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle>
            {editMode ? '✏️ Editar Configuración' : '➕ Nueva Configuración'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={tiposAuxiliar}
                  getOptionLabel={(option) => option.tipoAuxiliar || option.TipoAuxiliar || ''}
                  value={form.idTipoAuxiliar}
                  onChange={(e, newValue) => setForm(prev => ({ ...prev, idTipoAuxiliar: newValue }))}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Auxiliar *" size="small" fullWidth />
                  )}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={cuentas}
                  getOptionLabel={(option) => getCuentaDisplay(option)}
                  value={form.idCuenta}
                  onChange={(e, newValue) => setForm(prev => ({ ...prev, idCuenta: newValue }))}
                  isOptionEqualToValue={(option, value) => option.idCuenta === value.idCuenta}
                  renderInput={(params) => (
                    <TextField {...params} label="Cuenta Contable *" size="small" fullWidth />
                  )}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={cuentas}
                  getOptionLabel={(option) => getCuentaDisplay(option)}
                  value={form.idCuentaContrapartida}
                  onChange={(e, newValue) => setForm(prev => ({ ...prev, idCuentaContrapartida: newValue }))}
                  isOptionEqualToValue={(option, value) => option.idCuenta === value.idCuenta}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Cuenta Contrapartida (Opcional)" 
                      size="small" 
                      fullWidth 
                      helperText="Cuenta que se usará como contrapartida en los asientos automáticos"
                    />
                  )}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={opcionesTablasAuxiliares}
                  getOptionLabel={(option) => option.label}
                  value={opcionesTablasAuxiliares.find(opt => opt.value === form.tablaAuxiliar) || null}
                  onChange={(e, newValue) => setForm(prev => ({ ...prev, tablaAuxiliar: newValue?.value || '' }))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Tipo de Auxiliar *" 
                      size="small" 
                      fullWidth
                      helperText="Seleccione el tipo de auxiliar predefinido que manejará esta cuenta"
                    />
                  )}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.obligatorio}
                      onChange={(e) => setForm(prev => ({ ...prev, obligatorio: e.target.checked }))}
                      disabled={loading}
                    />
                  }
                  label="Auxiliar obligatorio en esta cuenta"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.activo}
                      onChange={(e) => setForm(prev => ({ ...prev, activo: e.target.checked }))}
                      disabled={loading}
                    />
                  }
                  label="Configuración activa"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || !tienePermiso('Configurar Auxiliares')}
            >
              {loading ? 'Guardando...' : (editMode ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default ConfiguracionAuxiliares;
